import React, { HTMLProps, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toPrice } from '../../../utils/parsers';
import { getPensionDate, isPensionCalculatedTogether } from '../../../utils/functions';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';
import { Button } from '../../general/Button';
import { useNetCalculation } from '../../../hooks/netGrossCalculations';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { Tooltip } from '../../general/Tooltip';
import { IconElements } from '../../../config/icons';
import { useAowWarning } from '../hooks/useAowWarning';
import { LAST_SECTION_INDEX, SUMMARY_SECTION_INDEX } from '../../../config/constants';
import { useSupplementWarning } from '../hooks/useSupplementWarning';
import { useLegalRetirementAge } from '../hooks/usePensionDate';
import { Link } from '../../general/Link';

export const Cocktail: React.FC<
    {
        isColorGoal?: boolean;
        pageNum?: number;
        isForPrinting?: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ isColorGoal = false, pageNum = 0, isForPrinting = false, className }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo, expensesInfo },
    } = useContext(PensionContext);
    const breakpoints = useWindowBreakpoints();
    const netIncome = useNetCalculation(pageNum);

    const total = netIncome?.netIncome.total.monthly ?? 0;
    const graphLimit = Math.ceil((Math.max(total, expensesInfo?.goal ?? 0) * 1.2) / 100000) * 100000;
    const withPartner = isPensionCalculatedTogether(personalInfo);
    return (
        <>
            <div
                className={`${className} text-sm sm:text-base w-full ${
                    isForPrinting ? '' : 'md:w-1/2 md:sticky sm:top-12'
                }`}
            >
                <div className="bg-white shadow-xl xl:rounded-lg">
                    <div className="mx-4 mt-4 md:mx-8 md:mt-8 xl:mb-4">
                        <CocktailTitle total={total} pageNum={pageNum} />
                        {withPartner &&
                            (pageNum === LAST_SECTION_INDEX || pageNum === SUMMARY_SECTION_INDEX) &&
                            !isForPrinting && <CocktailSelectPeriodButtons />}
                        <LoadingStateWrapper
                            isLoading={netIncome.isLoading}
                            isError={netIncome.isError}
                            isIdle={false}
                            isFetching={netIncome.isFetching}
                        >
                            <div className="grid grid-cols-2 gap-8">
                                <CocktailLegend className="pt-2 pb-2" isColorGoal={isColorGoal} pageNum={pageNum} />
                                <div className="">
                                    <div className="relative h-full grid grid-cols-8">
                                        <CocktailChart
                                            graphLimit={graphLimit}
                                            className="pt-2 pb-2"
                                            pageNum={pageNum}
                                            isForPrinting={isForPrinting}
                                        />
                                        {!(breakpoints.isMobile || breakpoints.isTablet) && (
                                            <CocktailAxis graphLimit={graphLimit} axesCount={4} />
                                        )}

                                        {(expensesInfo?.goal ?? 0) > 0 && (
                                            <GoalIndication
                                                isColorGoal={isColorGoal}
                                                goal={expensesInfo?.goal ?? 0}
                                                graphLimit={graphLimit}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </LoadingStateWrapper>
                    </div>
                    <ProgressBadge
                        goal={expensesInfo?.goal}
                        isColorGoal={isColorGoal}
                        className="h-auto"
                        pageNum={pageNum}
                    />
                </div>
                <div className="mt-5">
                    <Tooltip
                        className={`float-left ${isForPrinting ? 'hidden' : ''}`}
                        tooltipTitle={t('pension-cocktail-inflation-explanation-tooltip-title')}
                        tooltipText={t('pension-cocktail-inflation-explanation-tooltip-text')}
                    >
                        <p className="text-bnd-black-80 hover:text-bnd-black">
                            {t('pension-cocktail-inflation-explanation-title')}
                        </p>
                    </Tooltip>
                    <Link
                        className={`text-bnd-red float-right ${isForPrinting ? 'hidden' : ''}`}
                        href="https://media-bnd.s3-accelerate.amazonaws.com/2022/03/Uitgangspunten-pensioenplanner.pdf"
                        target="_blank"
                        title="pensioenplanner-assumptions"
                        rel="noreferrer"
                    >
                        {t('pension-cocktail-planner-assumptions')}
                    </Link>
                </div>
            </div>
        </>
    );
};

const CocktailTitle: React.FC<{
    total: number;
    pageNum: number;
}> = ({ total, pageNum }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo, expensesInfo, general },
    } = useContext(PensionContext);
    const withPartner = isPensionCalculatedTogether(personalInfo);
    const isSurplus = total > (expensesInfo?.goal ?? 0);
    const customerAndPartnerSamePensionDate =
        getPensionDate(personalInfo.self?.pensionAge ?? 0, personalInfo.self?.age ?? 0) ===
        getPensionDate(personalInfo.partner?.pensionAge ?? 0, personalInfo.partner?.age ?? 0);
    const amount = toPrice(Math.abs(total - (expensesInfo?.goal ?? 0)), {
        onlyFullEuros: true,
        withCurrencySymbol: true,
    });
    const colorText = isSurplus ? 'text-bnd-green' : 'text-bnd-red';
    const startFromPartnerPensionAge = general.startFromPartnerPensionAge;

    const legalRetirementAge = useLegalRetirementAge();
    const pensionAgeSelf = personalInfo.self?.pensionAge ?? 0;
    const pensionAgePartner = personalInfo.partner?.pensionAge ?? 0;

    const title = () => {
        if (pageNum !== LAST_SECTION_INDEX && pageNum !== SUMMARY_SECTION_INDEX)
            return (
                <Trans t={t} i18nKey={'pension-cocktail-fill-screen-cocktail-title'} values={{ amount: amount }}>
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        if (customerAndPartnerSamePensionDate && withPartner && !isSurplus) {
            if (pensionAgeSelf < legalRetirementAge || pensionAgePartner < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-both-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-both'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (customerAndPartnerSamePensionDate && withPartner && isSurplus) {
            if (pensionAgeSelf < legalRetirementAge || pensionAgePartner < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-both-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-both'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (!startFromPartnerPensionAge && !withPartner && !isSurplus) {
            if (pensionAgeSelf < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-nopartner-customer-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-nopartner-customer'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (!startFromPartnerPensionAge && !withPartner && isSurplus) {
            if (pensionAgeSelf < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-nopartner-customer-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-nopartner-customer'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (!startFromPartnerPensionAge && withPartner && !isSurplus) {
            if (pensionAgeSelf < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-customer-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-customer'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (!startFromPartnerPensionAge && withPartner && isSurplus) {
            if (pensionAgeSelf < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-customer-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-customer'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (startFromPartnerPensionAge && withPartner && !isSurplus) {
            if (pensionAgePartner < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-partner-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-shortage-partner-partner'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
        if (startFromPartnerPensionAge && withPartner && isSurplus) {
            if (pensionAgePartner < legalRetirementAge) {
                return (
                    <Trans
                        t={t}
                        i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-partner-no-aow'}
                        values={{ amount: amount }}
                    >
                        ...
                        <span className={colorText}>amount</span>
                        ...
                    </Trans>
                );
            }
            return (
                <Trans
                    t={t}
                    i18nKey={'pension-cocktail-fill-screen-cocktail-title-surplus-partner-partner'}
                    values={{ amount: amount }}
                >
                    ...
                    <span className={colorText}>amount</span>
                    ...
                </Trans>
            );
        }
    };

    return (
        <div className="mb-8 pt-4">
            <span className="font-medium text-2xl">{title()}</span>
        </div>
    );
};

const CocktailSelectPeriodButtons: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        actions,
        parameters: { general },
    } = useContext(PensionContext);

    return (
        <div className="flex flex-row gap-x-4 gap-y-2 flex-wrap mb-8">
            <div>
                <Button
                    className="px-4 font-medium"
                    buttonType={general.startFromPartnerPensionAge ? 'secondary' : 'primary'}
                    onClick={() => actions.setParameters({ general: { startFromPartnerPensionAge: false } })}
                >
                    {t('pension-cocktail-fill-screen-cocktail-btn')}
                </Button>
            </div>
            <div>
                <Button
                    className="px-4 font-medium"
                    buttonType={general.startFromPartnerPensionAge ? 'primary' : 'secondary'}
                    onClick={() => actions.setParameters({ general: { startFromPartnerPensionAge: true } })}
                >
                    {t('pension-cocktail-fill-screen-cocktail-btn-partner')}
                </Button>
            </div>
        </div>
    );
};

const CocktailChart: React.FC<
    { graphLimit: number; pageNum: number; isForPrinting?: boolean } & HTMLProps<HTMLDivElement>
> = ({ graphLimit, className, pageNum, isForPrinting }) => {
    const netCalculation = useNetCalculation(pageNum);

    const aowWarningScenario = useAowWarning();

    const supplementWarningScenario = useSupplementWarning();
    const showCocktailWarnings = [LAST_SECTION_INDEX, SUMMARY_SECTION_INDEX].includes(pageNum);

    const AowBlock = () => {
        const showWarningAow = showCocktailWarnings && aowWarningScenario.warning;
        const aowBackground = showWarningAow ? 'yellow-orange-striped-background' : 'bg-bnd-yellow';
        const flexValue = netCalculation.netAow.total.monthly;

        return (
            <div
                className={`rounded-lg shadow-inner-2 ${aowBackground} mt-1 items-center justify-center flex relative`}
                style={{
                    flex: isNaN(flexValue) ? 0 : flexValue,
                }}
            >
                {showWarningAow &&
                    (isForPrinting ? (
                        <IconElements.ExclamationmarkRounded className="absolute h-1/3" />
                    ) : (
                        <Tooltip
                            place="left"
                            className="h-full w-full items-center justify-center flex"
                            tooltipText={aowWarningScenario.text}
                        >
                            <IconElements.ExclamationmarkRounded className="absolute h-1/3" />
                        </Tooltip>
                    ))}
            </div>
        );
    };

    const SupplementsBlock = () => {
        const showWarningSupplement = showCocktailWarnings && supplementWarningScenario.warning;
        const supplementBackground = showWarningSupplement ? 'cyan-orange-striped-background' : 'bg-bnd-cyan-1';
        const flexValue = netCalculation.netSupplements.total.monthly;

        return (
            <div
                className={`rounded-lg shadow-inner-2 ${supplementBackground} mt-1 items-center justify-center flex relative`}
                style={{
                    flex: isNaN(flexValue) ? 0 : flexValue,
                }}
            >
                {showWarningSupplement &&
                    (isForPrinting ? (
                        <IconElements.ExclamationmarkRounded className="absolute h-1/3" />
                    ) : (
                        <Tooltip
                            place="left"
                            className="h-full w-full items-center justify-center flex"
                            tooltipElement={supplementWarningScenario.textNode}
                        >
                            <IconElements.ExclamationmarkRounded className="absolute h-1/3" />
                        </Tooltip>
                    ))}
            </div>
        );
    };

    return (
        <div className={`col-span-5 h-full ${className}`}>
            <div className="flex h-full rounded-lg border-4 border-bnd-white-1 bg-bnd-white-1 shadow-cocktail">
                <div className="z-10 justify-end items-stretch flex flex-col flex-1 shadow-inner-2 rounded-lg min-h-500">
                    <div style={{ flex: `${graphLimit - netCalculation.netIncome.total.monthly}` }} />
                    {netCalculation.netSalary.total.monthly > 0 && (
                        <div
                            className="rounded-lg shadow-inner-2 bg-bnd-peach mt-1"
                            style={{
                                flex: netCalculation.netSalary.total.monthly,
                            }}
                        />
                    )}
                    {netCalculation.netOtherIncome.total.monthly > 0 && (
                        <div
                            className="rounded-lg shadow-inner-2 bg-bnd-pink mt-1"
                            style={{
                                flex: netCalculation.netOtherIncome.total.monthly,
                            }}
                        />
                    )}
                    {netCalculation.netAdditionalSavingsAndInvestments.total.monthly > 0 && (
                        <div
                            className="rounded-lg shadow-inner-2 bg-bnd-cyan-2 mt-1"
                            style={{
                                flex: netCalculation.netAdditionalSavingsAndInvestments.total.monthly,
                            }}
                        />
                    )}
                    <SupplementsBlock />
                    {netCalculation.netEmployeePension.total.monthly > 0 && (
                        <div
                            className="rounded-lg shadow-inner-2 bg-bnd-blue mt-1"
                            style={{
                                flex: netCalculation.netEmployeePension.total.monthly,
                            }}
                        />
                    )}
                    <AowBlock />
                </div>
            </div>
        </div>
    );
};

const CocktailLegend: React.FC<{ isColorGoal: boolean; pageNum: number } & HTMLProps<HTMLDivElement>> = ({
    className,
    isColorGoal,
    pageNum,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo, personalInfo },
    } = useContext(PensionContext);

    const netCalculation = useNetCalculation(pageNum);
    const generalPM = t('general-pm');
    const goalDelta = (expensesInfo?.goal ?? 0) - netCalculation.netIncome.total.monthly;
    const showSalaryIfReceived = [LAST_SECTION_INDEX, SUMMARY_SECTION_INDEX].includes(pageNum);
    return (
        <div className={className}>
            {!isColorGoal && (
                <LegendItemDouble
                    title={t('pension-cocktail-fill-screen-cocktail-legend-surplus')}
                    className="mb-5"
                    colorLeft="bg-bnd-red"
                    colorRight="bg-bnd-green"
                />
            )}
            {isColorGoal && goalDelta > 0 && (
                <LegendItem
                    title={t('pension-cocktail-fill-screen-shortage')}
                    amount={goalDelta}
                    className="mb-5"
                    color="bg-bnd-red"
                    generalPM={generalPM}
                    notes={[]}
                />
            )}
            {isColorGoal && goalDelta < 0 && (
                <LegendItem
                    title={t('pension-cocktail-fill-screen-surplus')}
                    amount={-goalDelta}
                    className="mb-5"
                    color="bg-bnd-green"
                    generalPM={generalPM}
                    notes={[]}
                />
            )}
            {showSalaryIfReceived && netCalculation.netSalary.total.monthly > 0 && (
                <LegendItem
                    title={t('pension-cocktail-fill-screen-cocktail-legend-salary')}
                    amount={netCalculation.netSalary.total.monthly}
                    className="mb-5"
                    color="bg-bnd-peach"
                    generalPM={generalPM}
                    notes={
                        isPensionCalculatedTogether(personalInfo)
                            ? [
                                  {
                                      text: t('pension-cocktail-fill-screen-cocktail-legend-salary-self'),
                                      amount: netCalculation.netSalary.me.monthly,
                                  },
                                  {
                                      text: t('pension-cocktail-fill-screen-cocktail-legend-salary-partner'),
                                      amount: netCalculation.netSalary.partner.monthly,
                                  },
                              ]
                            : []
                    }
                />
            )}
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-other-income')}
                amount={netCalculation.netOtherIncome.total.monthly}
                className="mb-5"
                color="bg-bnd-pink"
                generalPM={generalPM}
                notes={[]}
            />
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-savings-and-investments')}
                amount={netCalculation.netAdditionalSavingsAndInvestments.total.monthly}
                className="mb-5"
                color="bg-bnd-cyan-2"
                generalPM={generalPM}
                notes={[
                    {
                        text: `Beleggen`,
                        amount: netCalculation.netAdditionalSavingsAndInvestments.me.investment.monthly,
                    },
                    {
                        text: `Sparen`,
                        amount: netCalculation.netAdditionalSavingsAndInvestments.me.saving.monthly,
                    },
                ]}
            />
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-supplement')}
                amount={netCalculation.netSupplements.total.monthly}
                className="mb-5"
                color="bg-bnd-cyan-1"
                generalPM={generalPM}
                notes={
                    isPensionCalculatedTogether(personalInfo)
                        ? [
                              {
                                  text: `${t('general-your')} pensioen`,
                                  amount: netCalculation.netSupplements.me.monthly,
                              },
                              {
                                  text: `${t('general-partner')} pensioen`,
                                  amount: netCalculation.netSupplements.partner.monthly,
                              },
                          ]
                        : []
                }
            />
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-employee-pension')}
                amount={netCalculation.netEmployeePension.total.monthly}
                className="mb-5"
                color="bg-bnd-blue"
                generalPM={generalPM}
                notes={
                    isPensionCalculatedTogether(personalInfo)
                        ? [
                              {
                                  text: `${t('general-your')} pensioen`,
                                  amount: netCalculation.netEmployeePension.me.monthly,
                              },
                              {
                                  text: `${t('general-partner')} pensioen`,
                                  amount: netCalculation.netEmployeePension.partner.monthly,
                              },
                          ]
                        : []
                }
            />
            <AOWLegendItem generalPM={generalPM} pageNum={pageNum} />
        </div>
    );
};

const AOWLegendItem: React.FC<{ generalPM: string; pageNum: number }> = ({ generalPM, pageNum }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo, general },
    } = useContext(PensionContext);

    const legalRetirementAge = useLegalRetirementAge();
    const netCalculation = useNetCalculation(pageNum);
    const yearsTillPension = general.startFromPartnerPensionAge
        ? personalInfo.partner.pensionAge - (personalInfo.partner.age ?? 0)
        : personalInfo.self.pensionAge - (personalInfo.self.age ?? 0);
    const ageSelf = personalInfo.self.age ?? 0;
    const agePartner = personalInfo.partner.age ?? 0;
    const calculatedTogether = isPensionCalculatedTogether(personalInfo);

    const notes = [];

    //if entitled to AOW
    if (ageSelf + yearsTillPension >= legalRetirementAge) {
        //if not calculated together no notes
        if (calculatedTogether) {
            notes.push({
                text: `${t('general-your')} ${t('pension-cocktail-fill-screen-cocktail-legend-aow')}`,
                amount: netCalculation.netAow.me.monthly,
            });
        }
        //if retired
    } else if (ageSelf + yearsTillPension >= personalInfo.self.pensionAge) {
        notes.push({
            text: `${t('pension-cocktail-fill-screen-cocktail-legend-from-your-aow')}`,
            amount: netCalculation.netAow.me.monthly,
        });
        //if partner gets full AOW or no AOW or if no partner insert "voor AOW-leeftijd 0"
        if (
            !calculatedTogether ||
            (calculatedTogether &&
                (agePartner + yearsTillPension >= legalRetirementAge ||
                    agePartner + yearsTillPension < personalInfo.partner.pensionAge))
        ) {
            notes.push({
                text: `${t('pension-cocktail-fill-screen-cocktail-legend-before-aow')}`,
                //-1 is a hacky implementation to display 0, since 0 needs to be filtered out for many places
                amount: -1,
            });
        }
    }

    if (calculatedTogether) {
        //if partner is entitled to AOW
        if (agePartner + yearsTillPension >= legalRetirementAge) {
            notes.push({
                text: `${t('general-partner')} ${t('pension-cocktail-fill-screen-cocktail-legend-aow')}`,
                amount: netCalculation.netAow.partner.monthly,
            });
            //if partner is retired
        } else if (agePartner + yearsTillPension >= personalInfo.partner.pensionAge) {
            notes.push(
                {
                    text: `${t('pension-cocktail-fill-screen-cocktail-legend-from-partner-aow')}`,
                    amount: netCalculation.netAow.partner.monthly,
                },
                {
                    text: `${t('pension-cocktail-fill-screen-cocktail-legend-before-aow')}`,
                    //-1 is a hacky implementation to display 0, since 0 needs to be filtered out for many places
                    amount: -1,
                },
            );
        }
    }

    return (
        <LegendItem
            title={t('pension-cocktail-fill-screen-cocktail-legend-aow')}
            amount={netCalculation.netAow.total.monthly}
            className="mb-5"
            color="bg-bnd-yellow"
            generalPM={generalPM}
            notes={notes}
        />
    );
};

const LegendItemDouble: React.FC<
    {
        title: string;
        colorLeft: string;
        colorRight: string;
    } & HTMLProps<HTMLDivElement>
> = ({ title, colorLeft, colorRight, className }) => {
    return (
        <div className={className}>
            <div className="col-span-5 flex items-center font-medium">
                <div
                    className={`rounded-full w-2 h-4 ${colorLeft}`}
                    style={{ borderBottomRightRadius: '100%', borderTopRightRadius: '100%' }}
                />
                <div
                    className={`rounded-full w-2 h-4 ${colorRight}`}
                    style={{ borderBottomLeftRadius: '100%', borderTopLeftRadius: '100%' }}
                />
                <div className="ml-4 flex-1">{title}</div>
            </div>
        </div>
    );
};

const LegendItem: React.FC<
    {
        title: string;
        amount: number;
        color: string;
        colorText?: string;
        generalPM: string;
        notes: { text: string; amount?: number }[];
    } & HTMLProps<HTMLDivElement>
> = ({ title, amount, color, generalPM, notes, colorText, className }) => {
    const filteredNotes = () => {
        return notes.filter((note) => note.amount !== undefined && note.amount !== 0 && note.amount !== undefined);
    };

    return (
        <div className={className}>
            <div className="flex items-center">
                <div className={`rounded-full w-4 h-4 ${amount > 0 ? 'bg-white' : color}`} />
                <div className="ml-4 flex-1 font-medium">{title}</div>
            </div>

            {amount > 0 && (
                <>
                    <div className="flex items-center">
                        <div className={`rounded-full w-4 h-4 ${color}`} />
                        <div className={`ml-4 flex-1 font-medium text-sm sm:text-lg ${colorText}`}>
                            {toPrice(+amount, { onlyFullEuros: true, withCurrencySymbol: true }) + ' ' + generalPM}
                        </div>
                    </div>

                    {filteredNotes().map((note) => {
                        return (
                            <div className="flex items-center" key={note.text}>
                                <div className="w-4 h-4 bg-white" />
                                <div className="ml-4 flex-1 opacity-50">{note.text}</div>
                                {note.amount !== undefined && (
                                    <div className="ml-4 flex-1 opacity-50">
                                        {/*-1 is a hacky implementation, to display a 0, since 0 needs to be filtered out for many places*/}
                                        {+note.amount !== -1
                                            ? toPrice(+note.amount, { onlyFullEuros: true, withCurrencySymbol: true })
                                            : toPrice(0, { onlyFullEuros: true, withCurrencySymbol: true })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

const CocktailAxis: React.FC<{ graphLimit: number; axesCount: number }> = ({ graphLimit, axesCount }) => {
    const displayAmount = (index: number) => {
        const amount = graphLimit - (graphLimit * (index + 1)) / (axesCount - 1);
        return isNaN(amount) ? 0 : amount;
    };

    const displayGraphLimit = isNaN(graphLimit) ? 0 : graphLimit;

    return (
        <div className="col-span-3 h-full py-3">
            <div className="h-full flex flex-col flex-1 opacity-60">
                {Array.from(Array(axesCount - 1).keys()).map((_, i) =>
                    i === 0 ? (
                        <div
                            key={i}
                            className="flex flex-col justify-between flex-1"
                            style={{ bottom: `${(100 / axesCount) * i * 100}` }}
                        >
                            <div className="flex flex-row items-start">
                                <div className="w-6 mr-2 border-b-2 border-bnd-black-40 border-dashed" />
                                <div className="relative">
                                    <span className="text-bnd-black-40 absolute -top-2.5">
                                        {toPrice(displayGraphLimit, {
                                            onlyFullEuros: true,
                                            withCurrencySymbol: true,
                                        })}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-row items-end">
                                <div className="w-6 mr-2 border-b-2 border-bnd-black-40 border-dashed" />
                                <div className="relative">
                                    <span className="text-bnd-black-40 absolute -bottom-2.5">
                                        {toPrice(displayAmount(i), {
                                            onlyFullEuros: true,
                                            withCurrencySymbol: true,
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            key={i}
                            className="flex flex-row items-end flex-1"
                            style={{ bottom: `${(100 / axesCount) * i * 100}` }}
                        >
                            <div className="w-6 mr-2 border-b-2 border-bnd-black-40 border-dashed" />
                            <div className="relative">
                                <span className="text-bnd-black-40 absolute -bottom-2.5">
                                    {toPrice(displayAmount(i), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}
                                </span>
                            </div>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};

const ProgressBadge: React.FC<{ goal?: number; isColorGoal: boolean; pageNum: number } & HTMLProps<HTMLDivElement>> = ({
    goal,
    isColorGoal,
    className,
    pageNum,
}) => {
    const { t } = useTranslation('pension');
    const netCalculation = useNetCalculation(pageNum);
    const goalValue = goal ?? 0;
    return (
        <div
            className={`${className} grid grid-cols-7 grid-rows-2 px-2 sm:px-8 pb-2 w-full bg-bnd-black-10  
            shadow-b-lg rounded-b-lg`}
        >
            <div className="flex items-end justify-around col-span-3">
                <span className="opacity-50 text-center">{t('pension-cocktail-fill-screen-total')}</span>
            </div>
            <div className="flex items-end justify-around col-start-5 col-span-3">
                <span className="opacity-50 text-center">{t('pension-cocktail-fill-screen-needed')}</span>
            </div>
            <span
                className={`font-medium text-xl sm:text-2xl text-center col-start-1 col-span-3 ${
                    isColorGoal
                        ? goalValue - netCalculation.netIncome.total.monthly > 0
                            ? 'text-bnd-yellow'
                            : 'text-bnd-green'
                        : ''
                }`}
            >
                {netCalculation.netIncome.total.monthly > 0
                    ? toPrice(netCalculation.netIncome.total.monthly, { onlyFullEuros: true, withCurrencySymbol: true })
                    : '-'}
            </span>
            <span className="opacity-50 text-3xl text-center">/</span>
            <span className="font-medium text-xl sm:text-2xl text-center col-span-3">
                {goalValue > 0 ? toPrice(goalValue, { onlyFullEuros: true, withCurrencySymbol: true }) : '-'}
            </span>
        </div>
    );
};

const GoalIndication: React.FC<{ goal: number; graphLimit: number; isColorGoal: boolean }> = ({
    goal,
    graphLimit,
    isColorGoal,
}) => {
    const { t } = useTranslation('pension');
    const computation = 10000 - (goal / graphLimit) * 10000;
    const computation2 = 10000 - (1 - goal / graphLimit) * 10000 - (goal === 0 ? 0 : 1);

    return (
        <div className="absolute w-full h-full flex flex-row py-3">
            <div className="w-full h-full flex flex-col flex-1">
                <div style={{ flex: !!computation ? computation : 0 }}></div>
                <div className="z-10 flex-1 grid grid-cols-8">
                    <div className={`col-span-6 border-b-2 ml-1 border-dashed border-bnd-black`}></div>
                    <div
                        className={`relative ${
                            isColorGoal
                                ? 'col-start-4 sm:col-start-5 xl:col-start-6 col-span-3'
                                : 'col-start-2 lg:col-start-3 xl:col-start-5 col-span-7 xl:col-span-5'
                        }`}
                    >
                        <div className={`absolute -top-3.5 rounded-lg ml-8 xl:ml-2 text-white px-5 bg-black`}>
                            {`${!isColorGoal ? `${t('pension-cocktail-fill-screen-cocktail-axis-goal-text')}` : ''}
                                ${toPrice(goal ?? 0, { onlyFullEuros: true, withCurrencySymbol: true })}`}
                        </div>
                    </div>
                </div>
                <div style={{ flex: !!computation2 ? computation2 : 0 }}></div>
            </div>
        </div>
    );
};
